import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getEmployeeService } from '../../../services/configServices'
import { getAllRoles } from '../../../services/admServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { PERMISSOES } from '../../../const/permissoes'
import { usePermissoes } from '../../../permissions'

/**
 * O catálogo real do aplicativo, agrupado por módulo.
 *
 * A chave sai sempre de `PERMISSOES`; o rótulo existe só para leitura humana.
 * Se uma permissão for criada no SSO, ela precisa entrar aqui também — senão a
 * tela deixa de descrever o que o sistema realmente controla.
 */
const CATALOGO = [
  {
    modulo: 'Painel',
    itens: [{ chave: PERMISSOES.DASHBOARD_VISUALIZAR, rotulo: 'Visualizar painel' }],
  },
  {
    modulo: 'Ordem de produção',
    itens: [
      { chave: PERMISSOES.ORDEM_VISUALIZAR, rotulo: 'Visualizar ordens' },
      { chave: PERMISSOES.ORDEM_CRIAR, rotulo: 'Criar ordem' },
      { chave: PERMISSOES.ORDEM_EDITAR, rotulo: 'Editar ordem' },
      { chave: PERMISSOES.ORDEM_CANCELAR, rotulo: 'Cancelar ordem' },
    ],
  },
  {
    modulo: 'Quebra de produção',
    itens: [
      { chave: PERMISSOES.QUEBRA_VISUALIZAR, rotulo: 'Visualizar quebras' },
      { chave: PERMISSOES.QUEBRA_INICIAR, rotulo: 'Iniciar quebra' },
      { chave: PERMISSOES.QUEBRA_CONCLUIR, rotulo: 'Concluir quebra' },
      { chave: PERMISSOES.QUEBRA_CANCELAR, rotulo: 'Cancelar quebra' },
    ],
  },
  {
    modulo: 'Chão de fábrica',
    itens: [
      { chave: PERMISSOES.KANBAN_VISUALIZAR, rotulo: 'Visualizar Kanban' },
      { chave: PERMISSOES.APONTAMENTO_REGISTRAR, rotulo: 'Registrar apontamento' },
    ],
  },
  {
    modulo: 'Produto',
    itens: [
      { chave: PERMISSOES.PRODUTO_VISUALIZAR, rotulo: 'Visualizar produtos' },
      { chave: PERMISSOES.PRODUTO_GERENCIAR, rotulo: 'Criar, editar e excluir produtos' },
    ],
  },
  {
    modulo: 'RFID',
    itens: [
      { chave: PERMISSOES.RFID_VISUALIZAR, rotulo: 'Visualizar RFID' },
      { chave: PERMISSOES.RFID_GERENCIAR, rotulo: 'Gerenciar associações' },
      { chave: PERMISSOES.RFID_BLOQUEAR, rotulo: 'Bloquear etiqueta' },
      { chave: PERMISSOES.RFID_REGISTRAR_EVENTO, rotulo: 'Registrar evento' },
    ],
  },
  {
    modulo: 'Fluxo de produção',
    itens: [
      { chave: PERMISSOES.FLUXO_VISUALIZAR, rotulo: 'Visualizar fluxos' },
      { chave: PERMISSOES.FLUXO_GERENCIAR, rotulo: 'Gerenciar fluxos e etapas' },
    ],
  },
  {
    modulo: 'Receita de máquina',
    itens: [
      { chave: PERMISSOES.RECEITA_VISUALIZAR, rotulo: 'Visualizar receitas' },
      { chave: PERMISSOES.RECEITA_GERENCIAR, rotulo: 'Gerenciar receitas e carga' },
    ],
  },
  {
    modulo: 'Relatórios',
    itens: [
      { chave: PERMISSOES.RELATORIO_VISUALIZAR, rotulo: 'Visualizar relatórios' },
      { chave: PERMISSOES.RELATORIO_EXPORTAR, rotulo: 'Exportar relatórios' },
    ],
  },
  {
    modulo: 'Administração',
    itens: [
      { chave: PERMISSOES.USUARIO_VISUALIZAR, rotulo: 'Visualizar usuários e permissões' },
      { chave: PERMISSOES.AUDITORIA_VISUALIZAR, rotulo: 'Visualizar auditoria' },
    ],
  },
  {
    modulo: 'Operação da plataforma',
    itens: [
      { chave: PERMISSOES.OBSERVABILIDADE_VISUALIZAR, rotulo: 'Visualizar observabilidade' },
      { chave: PERMISSOES.SINCRONIZACAO_VISUALIZAR, rotulo: 'Visualizar sincronização' },
      { chave: PERMISSOES.SINCRONIZACAO_EXECUTAR, rotulo: 'Executar sincronização' },
    ],
  },
]

const ITENS_DO_CATALOGO = CATALOGO.flatMap(grupo =>
  grupo.itens.map(item => ({ ...item, modulo: grupo.modulo }))
)

const getEmployeeName = employee => employee.fullName || employee.name || ''
const getRoleId = role => role.id || role.rolesId || role.name
const getRoleName = role => role.name || role.rolesName || ''

const Permissions = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()
  const { pode, ehAdministrador, emTransicao } = usePermissoes()

  const [tab, setTab] = useState(0)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [loadingEmployees, setLoadingEmployees] = useState(false)

  const [allRoles, setAllRoles] = useState([])
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [roleSearch, setRoleSearch] = useState('')

  const fetchEmployees = useCallback(async () => {
    if (!subCompanyId) {
      setEmployees([])
      return
    }
    setLoadingEmployees(true)
    try {
      const response = await getEmployeeService(subCompanyId)
      setEmployees(response.data.result || [])
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar os usuários da filial.'), 'error')
      setEmployees([])
    } finally {
      setLoadingEmployees(false)
    }
  }, [subCompanyId, notify])

  useEffect(() => {
    setSelectedEmployee(null)
    fetchEmployees()
  }, [fetchEmployees])

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true)
    try {
      const response = await getAllRoles()
      setAllRoles(response.data.result || [])
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar o catálogo de permissões.'), 'error')
      setAllRoles([])
    } finally {
      setLoadingRoles(false)
    }
  }, [notify])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  /** Roles do usuário selecionado vêm do próprio `/users` (perfil do SSO). */
  const userRoleNames = useMemo(
    () => (selectedEmployee && selectedEmployee.roles) || [],
    [selectedEmployee]
  )

  const groupedRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase()
    const filtered = allRoles.filter(role => getRoleName(role).toLowerCase().includes(term))
    const sorted = [...filtered].sort((a, b) => getRoleName(a).localeCompare(getRoleName(b)))
    return sorted.reduce((groups, role) => {
      const letter = (getRoleName(role)[0] || '#').toUpperCase()
      if (!groups[letter]) groups[letter] = []
      groups[letter].push(role)
      return groups
    }, {})
  }, [allRoles, roleSearch])

  const catalogoColumns = [
    {
      id: 'modulo',
      label: 'Módulo',
      width: 200,
      element: row => <Typography fontWeight={600}>{row.modulo}</Typography>,
    },
    { id: 'rotulo', label: 'Permissão', element: row => row.rotulo },
    {
      id: 'chave',
      label: 'Chave no SSO',
      element: row => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          {row.chave}
        </Typography>
      ),
    },
  ]

  const minhasColumns = [
    ...catalogoColumns,
    {
      id: 'tem',
      label: 'Você tem',
      width: 120,
      element: row =>
        pode(row.chave) ? (
          <Chip size="small" color="success" label="Sim" />
        ) : (
          <Chip size="small" variant="outlined" label="Não" />
        ),
    },
  ]

  return (
    <Box>
      <PageHeader title="Permissões" subtitle="Perfis de acesso e permissões por usuário" />

      <Tabs value={tab} onChange={(event, newTab) => setTab(newTab)} sx={{ mb: 3 }}>
        <Tab label="Catálogo" />
        <Tab label="Minhas permissões" />
        <Tab label="Permissões por usuário" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Estas são as permissões que o Controle de Produção reconhece. A atribuição a cada
            usuário é feita no painel do SSO Bihands.
          </Alert>
          <DataTable
            columns={catalogoColumns}
            rows={ITENS_DO_CATALOGO}
            pagination={false}
            rowKey={row => row.chave}
          />
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {emTransicao && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Você não tem nenhuma permissão deste sistema atribuída no SSO. Enquanto isso, o modo
              de transição libera todas as telas e ações — por isso tudo aparece como disponível
              abaixo. Assim que a primeira permissão for atribuída, apenas o que foi concedido
              continuará visível.
            </Alert>
          )}
          {!emTransicao && ehAdministrador && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Seu perfil no SSO é de administrador, então todas as ações ficam liberadas
              independentemente das permissões abaixo.
            </Alert>
          )}
          {!emTransicao && !ehAdministrador && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Abaixo, o que o seu usuário pode fazer neste sistema. Para pedir uma permissão nova,
              procure o responsável pelo SSO Bihands.
            </Alert>
          )}
          <DataTable
            columns={minhasColumns}
            rows={ITENS_DO_CATALOGO}
            pagination={false}
            rowKey={row => row.chave}
          />
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            A concessão e a remoção de permissões acontecem no painel do SSO Bihands. Aqui a
            visualização é somente leitura.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SelectSubCompany
                  value={subCompanyId}
                  onChange={event => setSubCompanyId(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  options={employees}
                  value={selectedEmployee}
                  loading={loadingEmployees}
                  disabled={!subCompanyId}
                  getOptionLabel={option => getEmployeeName(option)}
                  isOptionEqualToValue={(option, selected) => option.userId === selected.userId}
                  onChange={(event, newValue) => setSelectedEmployee(newValue)}
                  noOptionsText="Nenhum usuário encontrado"
                  renderInput={params => <TextField {...params} label="Usuário" />}
                />
              </Grid>
            </Grid>
          </Paper>

          {selectedEmployee && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Permissões de {getEmployeeName(selectedEmployee)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {userRoleNames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma permissão atribuída.
                  </Typography>
                ) : (
                  userRoleNames.map(role => (
                    <Chip size="small" color="primary" label={role} key={role} />
                  ))
                )}
              </Box>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Catálogo de permissões
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Buscar permissão"
              value={roleSearch}
              onChange={event => setRoleSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {loadingRoles && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loadingRoles && Object.keys(groupedRoles).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Nenhuma permissão encontrada.
              </Typography>
            )}

            {!loadingRoles && (
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {Object.entries(groupedRoles).map(([letter, roles]) => (
                  <Box key={letter} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {letter}
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Grid container spacing={0}>
                      {roles.map(role => (
                        <Grid item xs={12} sm={6} md={4} key={getRoleId(role)}>
                          <Chip
                            size="small"
                            sx={{ m: 0.25 }}
                            color={
                              userRoleNames.includes(getRoleName(role)) ? 'primary' : 'default'
                            }
                            variant={
                              userRoleNames.includes(getRoleName(role)) ? 'filled' : 'outlined'
                            }
                            label={getRoleName(role)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <FeedbackSnackbar />
    </Box>
  )
}

export default Permissions
